/**
 * The person's measuring units (Settings → Units) and the conversion of quantities into them. "auto" follows the device's region:
 * US customary in the United States, Liberia and Myanmar, metric everywhere else. A converted quantity always keeps the original
 * beside it, "360 ml (1½ cups)", so it is plain that the app changed it and what the source said.
 *
 * Teaspoons and tablespoons are left alone in both directions: metric kitchens use them too. A bare oven temperature ("425°") is
 * read as Fahrenheit from 300 up and as Celsius up to 260; between those it is left as written. When the source already gives both
 * systems ("750 g (26.5 oz)", "200 ml / ¾ cup", "30 × 30 cm (12 × 12 inches)"), nothing is converted: the author's own pair stays.
 */
export type MeasurementSystem = 'metric' | 'us'
export type MeasurementPreference = 'auto' | MeasurementSystem
export const MEASUREMENT_PREFERENCES: readonly MeasurementPreference[] = ['auto', 'metric', 'us']

const US_REGIONS = new Set(['US', 'LR', 'MM'])

/**
 * The region's system. Many people outside the US run their devices in "en-US", so a US language counts only when the time zone is
 * in the Americas too (or unknown); a device in Europe/Berlin with en-US is metric.
 */
export function systemForLocale(locale: string | undefined, timeZone: string | null | undefined = deviceTimeZone()): MeasurementSystem {
  try {
    const region = new Intl.Locale(locale || 'en-US').maximize().region
    if (!region || !US_REGIONS.has(region)) return 'metric'
    if (region === 'US' && timeZone && !/^(America|US|Pacific\/Honolulu|Pacific\/Guam|Pacific\/Pago_Pago)/.test(timeZone)) return 'metric'
    return 'us'
  } catch { return 'metric' }
}

function deviceTimeZone(): string | undefined {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return undefined }
}

export const normalizeMeasurementPreference = (value: unknown): MeasurementPreference =>
  value === 'metric' || value === 'us' ? value : 'auto'

export function resolveMeasurementSystem(preference: MeasurementPreference | undefined, locale = typeof navigator === 'undefined' ? 'en-US' : navigator.language, timeZone = deviceTimeZone()): MeasurementSystem {
  return preference === 'metric' || preference === 'us' ? preference : systemForLocale(locale, timeZone)
}

// ---- reading quantities ------------------------------------------------------------------------------------------------------------
const VULGAR: Record<string, number> = { '½': 0.5, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 0.25, '¾': 0.75, '⅕': 0.2, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 }
const NUMBER = String.raw`(?:\d+\s+\d+\/\d+|\d+\/\d+|\d+\s*[½⅓⅔¼¾⅕⅛⅜⅝⅞]|[½⅓⅔¼¾⅕⅛⅜⅝⅞]|\d+(?:[.,]\d+)?)`
const RANGE = String.raw`(${NUMBER})(?:\s*(?:-|–|to)\s*(${NUMBER}))?`

export function parseAmount(text: string): number | null {
  const t = text.trim()
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(t)
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3])
  const fraction = /^(\d+)\/(\d+)$/.exec(t)
  if (fraction) return Number(fraction[2]) ? Number(fraction[1]) / Number(fraction[2]) : null
  const vulgar = /^(\d*)\s*([½⅓⅔¼¾⅕⅛⅜⅝⅞])$/.exec(t)
  if (vulgar) return (vulgar[1] ? Number(vulgar[1]) : 0) + VULGAR[vulgar[2]!]!
  const plain = Number(t.replace(',', '.'))
  return Number.isFinite(plain) ? plain : null
}

// ---- writing quantities ------------------------------------------------------------------------------------------------------------
const FRACTIONS: Array<[number, string]> = [[0, ''], [0.125, '⅛'], [0.25, '¼'], [1 / 3, '⅓'], [0.5, '½'], [2 / 3, '⅔'], [0.75, '¾'], [1, '']]
/** Cups are measured in quarters and thirds; spoons go down to eighths. */
const CUP_FRACTIONS = FRACTIONS.filter(([value]) => value !== 0.125)
/** 1.5 → "1½", 0.33 → "⅓": the kitchen way to write a US amount. */
function friendly(value: number, steps = FRACTIONS): string {
  const whole = Math.floor(value), rest = value - whole
  const [fraction, glyph] = steps.reduce((best, entry) => (Math.abs(entry[0] - rest) < Math.abs(best[0] - rest) ? entry : best))
  const total = whole + (fraction === 1 ? 1 : 0)
  return total === 0 && glyph ? glyph : `${total || ''}${glyph}` || '0'
}
const roundTo = (value: number, step: number) => Math.round(value / step) * step
/** Metric amounts rounded the way a recipe writes them: to the nearest 5 from 10 up, whole below, one decimal under 1. */
const metric = (value: number) => (value >= 10 ? roundTo(value, 5) : value >= 1 ? Math.round(value) : Math.round(value * 10) / 10)
const short = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, ''))

type Converter = (amount: number) => { value: number; unit: string; text?: string }
interface Rule { pattern: string; to: MeasurementSystem; convert: Converter; temperature?: boolean; length?: boolean }

const millilitres = (ml: number) => (ml >= 1000 ? { value: ml / 1000, unit: 'L', text: short(Math.round(ml / 100) / 10) } : { value: metric(ml), unit: 'ml' })
const grams = (g: number) => (g >= 1000 ? { value: g / 1000, unit: 'kg', text: short(Math.round(g / 100) / 10) } : { value: metric(g), unit: 'g' })
const usVolume = (ml: number) => (ml < 15 ? { value: ml / 4.93, unit: 'tsp', text: friendly(ml / 4.93) } : ml < 60 ? { value: ml / 14.79, unit: 'tbsp', text: friendly(ml / 14.79) }
  : ml < 3785 ? { value: ml / 240, unit: ml / 240 > 1.01 ? 'cups' : 'cup', text: friendly(ml / 240, CUP_FRACTIONS) } : { value: ml / 3785, unit: 'gal', text: short(Math.round(ml / 378.5) / 10) })
const usWeight = (g: number) => (g < 454 ? { value: g / 28.35, unit: 'oz', text: short(roundTo(g / 28.35, 0.5)) } : { value: g / 453.6, unit: 'lb', text: short(Math.round(g / 45.36) / 10) })
const celsius = (f: number) => { const c = (f - 32) * 5 / 9; return { value: c, unit: '°C', text: String(c >= 100 ? roundTo(c, 5) : Math.round(c)) } }
const fahrenheit = (c: number) => { const f = c * 9 / 5 + 32; return { value: f, unit: '°F', text: String(f >= 200 ? roundTo(f, 5) : Math.round(f)) } }

const RULES: Rule[] = [
  // To metric.
  { pattern: String.raw`cups?\b`, to: 'metric', convert: a => millilitres(a * 240) },
  { pattern: String.raw`(?:fl\.?\s?oz|fluid\s+ounces?)\b`, to: 'metric', convert: a => millilitres(a * 29.57) },
  { pattern: String.raw`(?:oz|ounces?)\b`, to: 'metric', convert: a => grams(a * 28.35) },
  { pattern: String.raw`(?:lbs?|pounds?)\b`, to: 'metric', convert: a => grams(a * 453.6) },
  { pattern: String.raw`(?:quarts?|qts?)\b`, to: 'metric', convert: a => millilitres(a * 946) },
  { pattern: String.raw`(?:pints?|pts?)\b`, to: 'metric', convert: a => millilitres(a * 473) },
  { pattern: String.raw`(?:gallons?|gal)\b`, to: 'metric', convert: a => millilitres(a * 3785) },
  { pattern: String.raw`(?:inch(?:es)?)\b`, to: 'metric', convert: a => ({ value: a * 2.54 >= 10 ? Math.round(a * 2.54) : roundTo(a * 2.54, 0.5), unit: 'cm' }), length: true },
  { pattern: String.raw`(?:°\s?F|degrees?\s+F(?:ahrenheit)?)\b`, to: 'metric', convert: celsius, temperature: true },
  // To US customary.
  { pattern: String.raw`(?:kg|kilograms?|kilos?)\b`, to: 'us', convert: a => usWeight(a * 1000) },
  { pattern: String.raw`(?:g|grams?|gr)\b`, to: 'us', convert: a => usWeight(a) },
  { pattern: String.raw`(?:ml|millilit(?:er|re)s?)\b`, to: 'us', convert: a => usVolume(a) },
  { pattern: String.raw`(?:dl|decilit(?:er|re)s?)\b`, to: 'us', convert: a => usVolume(a * 100) },
  { pattern: String.raw`(?:cl|centilit(?:er|re)s?)\b`, to: 'us', convert: a => usVolume(a * 10) },
  { pattern: String.raw`(?:L|lit(?:er|re)s?)\b`, to: 'us', convert: a => usVolume(a * 1000) },
  { pattern: String.raw`(?:cm|centimet(?:er|re)s?)\b`, to: 'us', convert: a => ({ value: a / 2.54, unit: 'in', text: friendly(a / 2.54) }), length: true },
  { pattern: String.raw`(?:°\s?C|degrees?\s+C(?:elsius)?)\b`, to: 'us', convert: fahrenheit, temperature: true },
]

export interface Converted { text: string; count: number }

/** A quantity already written in `system` ("26.5 oz" for US customary). */
const writtenIn = (text: string, system: MeasurementSystem) => RULES.some(rule => rule.to !== system
  && new RegExp(String.raw`(?<![\p{L}\d.,])${RANGE}\s?(?:${rule.pattern})`, rule.pattern.startsWith('(?:L|') ? 'u' : 'iu').test(text))

/**
 * Whether the quantity at `offset` (with `length` characters) already has its value in `target` from the author: it sits inside
 * brackets or after a slash ("200 ml / ¾ cup"), is followed by one, or its line has a bracket holding a `target` amount
 * ("750 g zucchini (26.5 oz)"). Then nothing is converted.
 */
function givenBoth(text: string, offset: number, length: number, target: MeasurementSystem): boolean {
  const start = text.lastIndexOf('\n', offset - 1) + 1, end = text.indexOf('\n', offset) < 0 ? text.length : text.indexOf('\n', offset)
  const before = text.slice(start, offset)
  const open = (before.match(/\(/g) ?? []).length - (before.match(/\)/g) ?? []).length
  if (open > 0 || /\/\s*$/.test(before)) return true
  if (/^\s*(?:\(|\/\s*[\d½⅓⅔¼¾⅕⅛⅜⅝⅞])/.test(text.slice(offset + length))) return true
  return [...text.slice(start, end).matchAll(/\(([^()]*)\)/g)].some(group => writtenIn(group[1]!, target))
}

function write(rule: Rule, low: number, high: number | null): string {
  const a = rule.convert(low), b = high === null ? null : rule.convert(high)
  const show = (r: ReturnType<Converter>) => r.text ?? short(r.value)
  const gap = rule.temperature ? ' ' : ' '
  return b ? `${show(a)}–${show(b)}${gap}${b.unit}` : `${show(a)}${gap}${a.unit}`
}

/**
 * Every quantity in `text` written in the other system becomes "converted (original)". Text already in `target` is untouched, and so
 * is anything ambiguous: a unit without a number, a bare "425°" between 260 and 300.
 */
export function convertUnits(text: string, target: MeasurementSystem): Converted {
  let count = 0
  let out = text
  for (const rule of RULES.filter(r => r.to === target)) {
    // Units are matched case-insensitively except the litre's bare "L", which would otherwise catch every "l".
    const flags = (rule.pattern.startsWith('(?:L|') ? 'g' : 'gi') + 'u'
    // A size ("30 × 30 cm") is one measurement: each side converts and the unit is written once.
    if (rule.length) {
      const size = new RegExp(String.raw`(?<![\p{L}\d.,(])(${NUMBER})\s*[×xX]\s*(${NUMBER})(?:\s*[×xX]\s*(${NUMBER}))?\s?(${rule.pattern})`, flags)
      out = out.replace(size, (match, ...rest) => {
        const offset = rest[rest.length - 2] as number, whole = rest[rest.length - 1] as string
        const sides = [rest[0], rest[1], rest[2]].filter((side): side is string => typeof side === 'string').map(parseAmount)
        if (sides.some(side => side === null) || givenBoth(whole, offset, match.length, target)) return match
        const converted = sides.map(side => rule.convert(side!))
        count++
        return `${converted.map(side => side.text ?? short(side.value)).join(' × ')} ${converted[0]!.unit} (${match.trim()})`
      })
    }
    const pattern = new RegExp(String.raw`(?<![\p{L}\d.,(×])${RANGE}\s?(${rule.pattern})`, flags)
    out = out.replace(pattern, (match, low: string, high: string | undefined, _unit: string, offset: number, whole: string) => {
      const a = parseAmount(low), b = high ? parseAmount(high) : null
      if (a === null || (high && b === null) || givenBoth(whole, offset, match.length, target)) return match
      count++
      return `${write(rule, a, b)} (${match.trim()})`
    })
  }
  // A bare oven temperature: Fahrenheit from 300 up, Celsius up to 260.
  out = out.replace(/(?<![\p{L}\d])(\d{3})\s?°(?!\s?[CFcf])/gu, (match, digits: string, offset: number, whole: string) => {
    const value = Number(digits)
    if (givenBoth(whole, offset, match.length, target)) return match
    if (target === 'metric' && value >= 300) { count++; return `${celsius(value).text} °C (${match})` }
    if (target === 'us' && value <= 260 && value >= 100) { count++; return `${fahrenheit(value).text} °F (${match})` }
    return match
  })
  return { text: out, count }
}
